// YYYY-MM-DD in the user's own time zone. toISOString() is UTC, which turns an
// evening entry in the Americas into tomorrow's date.
export function localDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function todayLocal() {
  return localDateKey(new Date());
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function isRealCalendarDay(year, month, day) {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return false;
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const probe = new Date(year, month - 1, day);
  return probe.getFullYear() === year && probe.getMonth() === month - 1 && probe.getDate() === day;
}

// Display a stored YYYY-MM-DD without new Date('YYYY-MM-DD') (UTC midnight
// shifts a day earlier in US Eastern).
export function formatCalendarDay(isoDay, options) {
  if (isoDay == null || isoDay === '') return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoDay).trim());
  if (!match) return '';
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!isRealCalendarDay(year, month, day)) return '';
  return new Date(year, month - 1, day).toLocaleDateString('en-US', options);
}

// Bank-file / Excel cells to YYYY-MM-DD. parseDateCode is XLSX.SSF.parse_date_code
// (passed in so this helper stays free of a hard xlsx import in every caller).
export function parseImportCalendarDay(excelDate, parseDateCode) {
  try {
    if (excelDate === null || excelDate === undefined || excelDate === '') return null;

    if (typeof excelDate === 'number') {
      if (!Number.isFinite(excelDate) || typeof parseDateCode !== 'function') return null;
      const parts = parseDateCode(excelDate);
      if (!parts || !isRealCalendarDay(parts.y, parts.m, parts.d)) return null;
      return `${parts.y}-${pad2(parts.m)}-${pad2(parts.d)}`;
    }

    if (excelDate instanceof Date) {
      if (isNaN(excelDate.getTime())) return null;
      // SheetJS cellDates: CSV ISO -> UTC midnight; US M/D/YYYY -> local midnight.
      // Local getters on UTC midnight shift a day earlier in US zones.
      const utcMidnight = excelDate.getUTCHours() === 0
        && excelDate.getUTCMinutes() === 0
        && excelDate.getUTCSeconds() === 0
        && excelDate.getUTCMilliseconds() === 0;
      if (utcMidnight) {
        return `${excelDate.getUTCFullYear()}-${pad2(excelDate.getUTCMonth() + 1)}-${pad2(excelDate.getUTCDate())}`;
      }
      return localDateKey(excelDate);
    }

    const text = String(excelDate).trim();
    const isoDay = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s]|$)/.exec(text);
    if (isoDay) {
      const year = Number(isoDay[1]);
      const month = Number(isoDay[2]);
      const day = Number(isoDay[3]);
      if (!isRealCalendarDay(year, month, day)) return null;
      return `${isoDay[1]}-${isoDay[2]}-${isoDay[3]}`;
    }

    const mdy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s|$)/.exec(text);
    if (mdy) {
      const month = Number(mdy[1]);
      const day = Number(mdy[2]);
      const year = Number(mdy[3]);
      if (!isRealCalendarDay(year, month, day)) return null;
      return `${year}-${pad2(month)}-${pad2(day)}`;
    }

    const date = new Date(text);
    if (isNaN(date.getTime())) return null;
    return localDateKey(date);
  } catch (err) {
    return null;
  }
}

// A calendar day (no time) such as a due date, transaction date or import date.
// new Date('YYYY-MM-DD') is UTC midnight, which shows as the day before in the
// Americas. These helpers read a calendar day as the user's local day.
// Accepted: 'YYYY-MM-DD', a UTC-midnight ISO string ('YYYY-MM-DDT00:00:00.000Z',
// how a DATE column used to serialize), or a Date at UTC/local midnight.
// Anything with a real time of day is treated as a timestamp and left as is.
const CALENDAR_DAY_RE = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.0+)?(?:Z|[+-]00:?00)?)?$/;

export function calendarDayKey(value) {
  if (value == null || value === '') return '';
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return '';
    const utcMidnight = value.getUTCHours() === 0 && value.getUTCMinutes() === 0
      && value.getUTCSeconds() === 0 && value.getUTCMilliseconds() === 0;
    if (utcMidnight) {
      return `${value.getUTCFullYear()}-${pad2(value.getUTCMonth() + 1)}-${pad2(value.getUTCDate())}`;
    }
    return localDateKey(value);
  }
  const text = String(value).trim();
  const match = CALENDAR_DAY_RE.exec(text);
  if (match) {
    const [, y, m, d] = match;
    return isRealCalendarDay(Number(y), Number(m), Number(d)) ? `${y}-${m}-${d}` : '';
  }
  const parsed = new Date(text);
  return isNaN(parsed.getTime()) ? '' : localDateKey(parsed);
}

// Date object for display/comparison. Calendar days become local midnight;
// real timestamps are parsed normally. Invalid input gives an Invalid Date,
// exactly like new Date(value) would.
export function parseCalendarDay(value) {
  if (value instanceof Date) {
    const key = calendarDayKey(value);
    if (!key) return new Date(NaN);
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  if (value == null || value === '') return new Date(NaN);
  const text = String(value).trim();
  const match = CALENDAR_DAY_RE.exec(text);
  if (match) {
    const y = Number(match[1]);
    const m = Number(match[2]);
    const d = Number(match[3]);
    return isRealCalendarDay(y, m, d) ? new Date(y, m - 1, d) : new Date(NaN);
  }
  return new Date(text);
}
